import { Edit, X } from 'lucide-react'
import Link from 'next/link'
import * as React from 'react'

import { hasPageEditorRole } from '@/lib/page-editor-access'

import { EDITABLE_PAGES, getEditPath } from '@/configs/editable-pages-config'
import { useAuth } from '@/contexts/auth-context'
import { useCan } from '@/contexts/feature-flag-context'

interface AdminQuickEditFabProps {
  /** Slug of the current page being viewed (e.g. "homepage", "learning-path") */
  currentPageSlug: string
}

export default function AdminQuickEditFab({ currentPageSlug }: AdminQuickEditFabProps) {
  const { user } = useAuth()
  const canEdit = useCan('mode.editor')
  const [isOpen, setIsOpen] = React.useState(false)
  const menuRef = React.useRef<HTMLDivElement>(null)
  const toggle = React.useCallback(() => setIsOpen(prev => !prev), [])

  React.useEffect(() => {
    if (!isOpen) return

    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  if (!canEdit || !hasPageEditorRole(user)) return null

  return (
    <div ref={menuRef} className="fixed bottom-6 right-6 z-[120]">
      {isOpen && (
        <div
          id="admin-page-editor-menu"
          className="absolute bottom-16 right-0 w-64 origin-bottom-right overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_20px_60px_rgba(15,23,42,0.22)] animate-in fade-in slide-in-from-bottom-2"
        >
          <div className="px-1 py-1">
            <p className="px-3 pb-2 pt-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
              Edit Pages
            </p>
            {EDITABLE_PAGES.map(page => {
              const isCurrent = page.slug === currentPageSlug
              return (
                <Link
                  key={page.slug}
                  href={getEditPath(page.slug)}
                  className={`flex items-center rounded-xl px-3 py-2.5 text-sm transition-colors ${
                    isCurrent
                      ? 'bg-blue-50 font-semibold text-blue-700'
                      : 'text-slate-700 hover:bg-slate-100 hover:text-slate-950'
                  }`}
                >
                  {page.label}
                </Link>
              )
            })}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={toggle}
        className="flex size-14 items-center justify-center rounded-full border border-white/30 bg-blue-600 text-white shadow-[0_12px_32px_rgba(37,99,235,0.4)] transition-all hover:-translate-y-0.5 hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        aria-label="Admin quick actions"
        aria-expanded={isOpen}
        aria-controls="admin-page-editor-menu"
      >
        {isOpen ? <X className="size-5" aria-hidden="true" /> : <Edit className="size-5" aria-hidden="true" />}
      </button>
    </div>
  )
}
